import { gql } from '@apollo/client'

export const PROMOTION_APPLICATION_FIELDS = gql`
  fragment PromotionApplicationFields on PromotionApplication {
    applied {
      promotionId
      name
      discountMinor
      currency
      distribution
      allocations {
        roomStayReference
        discountMinor
        currency
      }
    }
    totalDiscountMinor
    currency
    state
  }
`

/**
 * Ask whether a code works on this cart, WITHOUT reserving anything.
 *
 * This is what the code field was missing. Previously the only way to learn a code had
 * failed was makePrimitiveReservation's ERROR path, where the answer arrived as one of
 * four generic strings — the BFF maps gRPC status codes there, which is right for
 * transport failures and wrong for a business outcome. The promotion service
 * distinguishes roughly eighteen reasons and translates each one; `reasonText` is that
 * sentence, and it is rendered as-is. Do not map `reason` to copy here.
 *
 * `guest` is optional and is the only way a per-guest cap can be ANSWERED rather than
 * deferred. The booker's phone is not collected until the details step, so the first
 * call omits it and a capped offer reports that it needs guest details.
 */
export const EVALUATE_PROMOTION_QUERY = gql`
  query EvaluatePromotion(
    $cartID: String!
    $intent: PromotionIntentInput!
    $guest: GuestIdentityInput
  ) {
    evaluatePromotion(cartID: $cartID, intent: $intent, guest: $guest) {
      totalDiscountMinor
      currency
      applied {
        promotionId
        name
        distribution
        discountMinor
        currency
        appliedLineIds
        allocations {
          roomStayReference
          discountMinor
          currency
        }
      }
      skipped {
        promotionId
        name
        reason
        reasonText
        # The service marks the entry the entered code named. Without it the client
        # guesses by reason and falls back to array position, which nothing orders.
        matchedSuppliedCode
      }
    }
  }
`

/**
 * The offers that apply to this cart, so a booker can SEE their discounts instead of only
 * being able to type one they already know.
 *
 * The panel has shown agents this list all along. Its absence here was the difference
 * between "enter a code if you have one" and "here is what you can have" — and a booker is
 * the one person who cannot be told a code out of band.
 *
 * Cart-aware, not a catalogue: the service filters by scope, schedule, currency and live
 * caps against this cart's own lines, so a listed offer is one that applies right now.
 * `conditions` is deliberately NOT selected — every condition in it has already been
 * satisfied for this cart, and requesting it is what invited three separate clients to
 * re-derive eligibility from it.
 */
export const ADVERTISED_PROMOTIONS_QUERY = gql`
  query AdvertisedPromotions($cartID: String!) {
    advertisedPromotions(cartID: $cartID) {
      promotionId
      name
      code
      discountType
      discountValue
      currency
      requiresGuestIdentity
    }
  }
`
